<template>
  <div class="flex flex-col gap-12 md:flex-row md:items-center">
    <RevInputText
      id="stock-input"
      v-model="localStock"
      class="md:w-[8rem]"
      data-qa="stock"
      :label="i18n(translations.stock)"
      type="number"
      @keyup.enter="handleSave"
    />

    <div
      v-if="hasOneOrLessMarket || referenceMarket"
      class="flex flex-col gap-12 md:flex-row md:items-center"
    >
      <RevInputText
        v-if="hasBackpricerEnabled"
        :id="`minimum-price-${listing.sku}`"
        v-model.number="minimumPrice"
        class="md:w-[8rem]"
        :label="
          i18n(translations.minimumPrice, {
            country: referenceMarket,
            currency: selectedReferenceMarketCurrency,
          })
        "
        type="number"
        @keyup.enter="handleSave"
      />

      <RevInputText
        :id="`price-${listing.sku}`"
        v-model.number="price"
        class="md:w-[8rem]"
        :label="
          hasBackpricerEnabled
            ? i18n(translations.targetPrice, {
                country: referenceMarket,
                currency: selectedReferenceMarketCurrency,
              })
            : i18n(translations.price, {
                country: referenceMarket,
                currency: selectedReferenceMarketCurrency,
              })
        "
        type="number"
        @keyup.enter="handleSave"
      />

      <div>
        <span id="backbox-price" class="caption mr-4 self-start">
          {{ backboxInfo.label }}
        </span>
        <br />
        <span aria-labelledby="backbox-price" class="body-2">
          {{ backboxInfo.value }}
        </span>
      </div>
    </div>

    <RevButton
      v-if="isEditing"
      :disabled="!canSave"
      :loading="isSaving"
      size="small"
      variant="secondary"
      @click="handleSave"
    >
      {{ i18n(translations.save) }}
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref, watch } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type {
  Listing,
  MarketListing,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevButton } from '@ds/components/Button'
import { RevInputText } from '@ds/components/InputText'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import { useListings } from '~/scopes/listings/pages/index/composables/useListings'

import translations from '../MinifiedInfo.translations'

const { openErrorToast } = useTheToast()
const { allMarkets } = useMarketplace()
const { updateListing, formatListingToTrackingEvent } = useListings()
const isSaving = ref(false)
const i18n = useI18n()

const tracking = useTracking()
const props = defineProps<{
  listing: Listing
  referenceMarket: MarketCountryCode | ''
  hasBackpricerEnabled: boolean
}>()

const { activeMarkets } = useSellerInfoStore()

const selectedReferenceMarketCurrency = computed(() => {
  return props.referenceMarket
    ? allMarkets[props.referenceMarket as MarketCountryCode]?.defaultCurrency ||
        ''
    : ''
})

// Use activeMarkets.length instead of hasOnlyOneMarket from store
// because UK sellers (previously in EU) have non-empty potentialMarkets.
// And since the hasOnlyOneMarket rely on the potentialMarkets + activeMarkets length info,
// it causes hasOnlyOneMarket to return false incorrectly in the UK case.
const hasOneOrLessMarket = ref(activeMarkets.length <= 1)

const marketInfo = computed(() => {
  if (!props.referenceMarket) return null

  return (
    props.listing.markets.offline[props.referenceMarket] ||
    props.listing.markets.withBackbox[props.referenceMarket] ||
    props.listing.markets.withoutBackbox[props.referenceMarket]
  )
})

const localStock = ref(props.listing.quantity)
const minimumPrice = ref(marketInfo.value?.minimumPrice?.amount)
const price = ref(marketInfo.value?.price?.amount)

const isEditing = computed(() => {
  return (
    props.listing.quantity !== localStock.value ||
    price.value !== marketInfo.value?.price?.amount ||
    minimumPrice.value !== marketInfo.value?.minimumPrice?.amount
  )
})

const canSave = computed(() => isEditing.value && localStock.value)

const backboxInfo = computed(() => {
  return {
    label: i18n(translations.backboxPrice, { country: props.referenceMarket }),
    value: marketInfo.value?.backboxPrice
      ? i18n.price(marketInfo.value.backboxPrice)
      : '–',
  }
})

async function handleSave() {
  if (!canSave.value) return

  try {
    isSaving.value = true

    const bodyMarket = props.referenceMarket
      ? {
          [props.referenceMarket]: {
            ...insertIf(Boolean(minimumPrice.value), {
              minimumPrice: {
                amount: Number(minimumPrice.value),
                currency:
                  allMarkets[props.referenceMarket as MarketCountryCode]
                    .defaultCurrency,
              },
            }),
            ...insertIf(Boolean(price.value), {
              price: {
                amount: Number(price.value),
                currency:
                  allMarkets[props.referenceMarket as MarketCountryCode]
                    .defaultCurrency,
              },
            }),
          },
        }
      : null

    tracking.trackClick({
      page_type: 'listings',
      zone: 'listing_price_update',
      name: 'save_shortchut',
      ...formatListingToTrackingEvent(
        props.listing,
        bodyMarket
          ? ({
              ...bodyMarket[props.referenceMarket],
              market: props.referenceMarket as MarketCountryCode,
            } as MarketListing & { market: MarketCountryCode })
          : null,
      ),
    })

    await updateListing(props.listing.id, {
      ...insertIf(Boolean(localStock.value), {
        quantity: localStock.value,
      }),
      ...insertIf(Boolean(bodyMarket), {
        markets: {
          ...bodyMarket,
        },
      }),
    })
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isSaving.value = false
  }
}

watch(
  () => props.referenceMarket,
  () => {
    price.value = marketInfo.value?.price?.amount
    minimumPrice.value = marketInfo.value?.minimumPrice?.amount
  },
)
</script>
